import React from 'react';

// Inline single-route rate-limit blurb for the troubleshooting blocks on
// /docs/errors/rate-limit-exceeded/. Pure view: the MDX page imports the
// build-time-filtered fixture once and threads `rows` to every call site,
// matching <RateLimitTable>'s "MDX owns the import" pattern. This keeps
// .tsx out of any module-resolution dependency on the .gitignored
// rate-limits-filtered.json artifact (which doesn't exist until
// `make filter-rate-limits` runs in the build pipeline).

export type RateLimitScope = 'item' | 'client' | 'both';

export type RateLimitExceededErrorCode =
  | 'ACCOUNTS_LIMIT'
  | 'ACCOUNTS_BALANCE_GET_LIMIT'
  | 'AUTH_LIMIT'
  | 'BALANCE_LIMIT'
  | 'IDENTITY_LIMIT'
  | 'INSTITUTIONS_GET_LIMIT'
  | 'INSTITUTIONS_GET_BY_ID_LIMIT'
  | 'INVESTMENTS_HOLDINGS_GET_LIMIT'
  | 'INVESTMENTS_LIMIT'
  | 'ITEM_GET_LIMIT'
  | 'TRANSACTIONS_LIMIT'
  | 'TRANSACTIONS_REFRESH_LIMIT'
  | 'TRANSACTIONS_SYNC_LIMIT';

export interface RateLimitExceededErrorLimit {
  path: string;
  scope?: RateLimitScope;
}

export const RATE_LIMIT_EXCEEDED_ERROR_LIMITS: Record<
  RateLimitExceededErrorCode,
  RateLimitExceededErrorLimit
> = {
  ACCOUNTS_LIMIT: { path: '/accounts/get' },
  ACCOUNTS_BALANCE_GET_LIMIT: {
    path: '/accounts/balance/get',
    scope: 'client',
  },
  AUTH_LIMIT: { path: '/auth/get', scope: 'item' },
  BALANCE_LIMIT: { path: '/accounts/balance/get', scope: 'item' },
  IDENTITY_LIMIT: { path: '/identity/get', scope: 'item' },
  INSTITUTIONS_GET_LIMIT: { path: '/institutions/get', scope: 'client' },
  INSTITUTIONS_GET_BY_ID_LIMIT: {
    path: '/institutions/get_by_id',
    scope: 'client',
  },
  INVESTMENTS_HOLDINGS_GET_LIMIT: { path: '/investments/holdings/get' },
  INVESTMENTS_LIMIT: { path: '/investments/transactions/get', scope: 'item' },
  ITEM_GET_LIMIT: { path: '/item/get' },
  TRANSACTIONS_LIMIT: { path: '/transactions/get' },
  TRANSACTIONS_REFRESH_LIMIT: {
    path: '/transactions/refresh',
    scope: 'client',
  },
  TRANSACTIONS_SYNC_LIMIT: { path: '/transactions/sync' },
};

interface LimitCell {
  display: string;
  limits: Array<unknown>;
}

interface RateLimitEnv {
  item: LimitCell;
  client: LimitCell;
}

export interface RateLimitForRow {
  path: string;
  display_path: string;
  production: RateLimitEnv;
  sandbox: RateLimitEnv;
}

interface Props {
  /** Endpoint path key (must match `row.path` in rate-limits-filtered.json). */
  path: string;
  /**
   * Which limits to render. Defaults to both per-Item and per-client.
   * Use `'item'` or `'client'` when an error block scopes to one dimension
   * (e.g. ACCOUNTS_BALANCE_GET_LIMIT is client-only; BALANCE_LIMIT is item-only).
   */
  scope?: RateLimitScope;
  /**
   * Rows from the build-time-filtered fixture. The MDX page (and tests)
   * pass this; the component does not import the fixture itself.
   */
  rows: RateLimitForRow[];
}

interface ErrorProps {
  /** RATE_LIMIT_EXCEEDED error code whose endpoint/scope mapping should render. */
  error: RateLimitExceededErrorCode;
  rows: RateLimitForRow[];
}

const formatEnv = (env: RateLimitEnv, scope: RateLimitScope): string | null => {
  const parts: string[] = [];
  if (scope !== 'client' && env.item.display !== 'N/A') {
    parts.push(`${env.item.display} (per Item)`);
  }
  if (scope !== 'item' && env.client.display !== 'N/A') {
    parts.push(`${env.client.display} (per client)`);
  }
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  return `${parts[0]} and ${parts[1]}`;
};

const RateLimitFor = ({
  path,
  scope = 'both',
  rows,
}: Props): React.ReactElement | null => {
  const row = rows.find((r) => r.path === path);
  if (!row) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `<RateLimitFor>: no row in rate-limits-filtered.json for path=${path}. ` +
          `Either the path is misspelled, the route was filtered out (Category B/F — ` +
          `see RateLimitTable/README.md), or the producer dropped it.`,
      );
    }
    return null;
  }
  const prod = formatEnv(row.production, scope);
  const sandbox = formatEnv(row.sandbox, scope);
  if (!prod && !sandbox) return null;
  return (
    <>
      {prod && (
        <>
          In Production, requests to <code>{row.display_path}</code> are rate
          limited to {prod}.
        </>
      )}
      {prod && sandbox && ' '}
      {sandbox && <>In the Sandbox, they are rate limited to {sandbox}.</>}
    </>
  );
};

export const RateLimitForError = ({
  error,
  rows,
}: ErrorProps): React.ReactElement | null => {
  const mapping = RATE_LIMIT_EXCEEDED_ERROR_LIMITS[error];
  return <RateLimitFor path={mapping.path} scope={mapping.scope} rows={rows} />;
};

export default RateLimitFor;
